import { amountToIndianWords } from "../amountInWords";
import { shouldShowDiscountColumn } from "../discountColumn";

export const cleanInvoiceValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

export const toInvoiceNumber = (value) => {
  const nextValue = Number(String(value || "0").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(nextValue) ? nextValue : 0;
};

export const formatInvoiceMoney = (value) =>
  `₹ ${toInvoiceNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export const getInvoiceStatus = (invoice = {}) => {
  const raw = String(invoice.status || "").toLowerCase();
  if (raw.includes("paid")) return { label: invoice.status || "Paid", tone: "paid" };
  if (raw.includes("overdue")) return { label: invoice.status || "Overdue", tone: "overdue" };
  if (raw.includes("pending")) return { label: invoice.status || "Pending", tone: "pending" };

  const dueAmount = toInvoiceNumber(invoice.receivables || invoice.balance || 0);
  if (dueAmount <= 0 && toInvoiceNumber(invoice.total) > 0) return { label: "Paid", tone: "paid" };
  return { label: invoice.status || "Pending", tone: "pending" };
};

const imageUrl = (imageName) => {
  if (!imageName) return "";
  if (/^https?:\/\//i.test(imageName)) return imageName;
  return encodeURI(`https://documents.zarpsoftware.in/zarp_images/${imageName}`);
};

const joinAddress = (...parts) => parts.filter(Boolean).join(", ");

const normalizeRecord = (value) => {
  if (Array.isArray(value)) return value[0] || {};
  if (!value || typeof value !== "object") return {};
  if (value.responseMessage && value.responseMessage !== value) {
    return normalizeRecord(value.responseMessage);
  }
  return value;
};

const firstBankValue = (bank, keys) => {
  const key = keys.find(
    (item) => bank[item] !== undefined && bank[item] !== null && bank[item] !== "",
  );
  return key ? bank[key] : "";
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const sumExistingFields = (rows, keys) =>
  rows.reduce(
    (sum, row) =>
      sum +
      keys.reduce((fieldSum, key) => {
        const value = row && row[key] !== undefined ? row[key] : 0;
        return fieldSum + toInvoiceNumber(value);
      }, 0),
    0,
  );

export const buildInvoicePrintModel = ({
  selectedInvoice = {},
  itemRows = [],
  customerData = {},
  userDetails = {},
  gstData = {},
  bankData = {},
  status,
} = {}) => {
  const normalizedBank = normalizeRecord(bankData);
  const rows = itemRows.length ? itemRows : selectedInvoice.invoiceNumber ? [selectedInvoice] : [];
  const companyGstin = gstData.businessRegistered === "YES" ? gstData.gstin : "";
  const customerGstin = customerData.gstin || customerData.gstNumber || selectedInvoice.customerGstNumber;
  const isGstInvoice = gstData.businessRegistered === "YES" && Boolean(companyGstin);

  const showDiscount = shouldShowDiscountColumn(selectedInvoice, rows);
  const showHsn = isGstInvoice;

  const cgst = toInvoiceNumber(selectedInvoice.cgst) || sumExistingFields(rows, ["cgst"]);
  const sgst = toInvoiceNumber(selectedInvoice.sgst) || sumExistingFields(rows, ["sgst"]);
  const igst = toInvoiceNumber(selectedInvoice.igst) || sumExistingFields(rows, ["igst"]);
  const totalTax = cgst + sgst + igst;
  const itemSubtotal = rows.reduce((sum, item) => {
    const amount = item.amount;
    if (hasValue(amount)) return sum + toInvoiceNumber(amount);
    return sum + toInvoiceNumber(item.quantity || item.qty || 1) * toInvoiceNumber(item.rate || item.price || 0);
  }, 0);
  const subtotal =
    selectedInvoice.subtotal ||
    selectedInvoice.subTotal ||
    selectedInvoice.taxableAmount ||
    (itemSubtotal ? itemSubtotal : selectedInvoice.total);

  return {
    title: isGstInvoice ? "TAX INVOICE" : "INVOICE",
    isGstInvoice,
    showDiscount,
    showHsn,
    status: status || getInvoiceStatus(selectedInvoice),
    company: {
      name: userDetails.companyName || userDetails.organizationName || userDetails.name || "Company",
      logoUrl: imageUrl(userDetails.logo || userDetails.imageName || userDetails.companyLogo || userDetails.logoName),
      signatureUrl: imageUrl(userDetails.signImage || userDetails.sign_image || userDetails.signature || userDetails.signatureImage),
      stampUrl: imageUrl(userDetails.stampImage || userDetails.stamp_image || userDetails.stamp || userDetails.companyStamp),
      gstin: companyGstin,
      phone: userDetails.contact,
      email: userDetails.email,
      address: joinAddress(userDetails.address1, userDetails.address2, userDetails.city, userDetails.state, userDetails.zipcode),
    },
    customer: {
      name: selectedInvoice.customerName || customerData.customerName || customerData.companyName,
      address: joinAddress(
        customerData.billAddress1,
        customerData.billAddress2,
        customerData.billCity,
        customerData.billState,
        customerData.billPincode,
        customerData.billCountry,
      ),
      gstin: customerGstin,
      phone: customerData.billPhone || customerData.phone || customerData.mobile,
      email: customerData.email,
    },
    invoice: {
      number: selectedInvoice.invoiceNumber,
      date: selectedInvoice.invoiceDate,
      dueDate: selectedInvoice.dueDate,
      terms: selectedInvoice.paymentTerms || selectedInvoice.terms,
      orderNumber: selectedInvoice.orderNumber || selectedInvoice.referenceNumber,
      paymentStatus: getInvoiceStatus(selectedInvoice).label,
    },
    items: rows.map((item, index) => ({
      id: `${item.id || item.itemId || item.itemsDetails || item.itemName || index}-${index}`,
      serial: index + 1,
      name: item.itemsDetails || item.itemName || item.name,
      hsn: item.hsnCode || item.hsnSac,
      quantity: item.quantity,
      rate: item.rate,
      discount: item.discount || item.rowDiscount,
      discountType: item.discountType || selectedInvoice.totalDiscountType,
      taxRate: item.tax,
      cgst: item.cgst,
      sgst: item.sgst,
      igst: item.igst,
      amount: item.amount,
    })),
    totals: {
      subtotal,
      adjustment: selectedInvoice.adjustment,
      adjustmentLabel: selectedInvoice.adjustmentLabel || "Adjustment",
      cgst,
      sgst,
      igst,
      totalTax,
      grandTotal: selectedInvoice.total,
      balanceDue: selectedInvoice.receivables || selectedInvoice.balance,
      amountInWords: selectedInvoice.amountInWords || selectedInvoice.totalInWords || amountToIndianWords(selectedInvoice.total),
    },
    bank: {
      upiId: firstBankValue(normalizedBank, ["upiId", "upi"]),
      accountName: firstBankValue(normalizedBank, ["name", "accountName"]),
      bankName: firstBankValue(normalizedBank, ["bankName", "bank", "branch"]),
      branchName: firstBankValue(normalizedBank, ["branchName"]),
      accountNumber: firstBankValue(normalizedBank, ["accountNumber", "accountNo"]),
      ifsc: firstBankValue(normalizedBank, ["ifsc", "ifscCode"]),
      currency: firstBankValue(normalizedBank, ["currency"]),
      accountType: firstBankValue(normalizedBank, ["accountType", "type"]),
    },
    notes: selectedInvoice.customerNote,
    termsAndCondition:
      selectedInvoice.termsAndCondition ||
      selectedInvoice.termsAndConditions ||
      selectedInvoice.terms_and_condition,
  };
};

export const invoiceFileName = (invoice = {}) => {
  const number = cleanInvoiceValue(invoice.invoice?.number, "invoice");
  const safe = (value) => String(value).replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
  return `Invoice_${safe(number)}.pdf`;
};
